// Retry acotado de sanado de auto-servicio (T3) — orquestador puro e inyectable.
//
// Cuando entra un pedido de mesa aún sin enriquecer (`self_service:null`), el backend materializa
// la asociación (`OrderItem`) unos instantes después. En vez de dejar la tarjeta suelta para
// siempre, re-consultamos el pedido con backoff acotado; en cuanto el read trae `order_uuid`,
// mergeamos `self_service` en el store y cancelamos los intentos restantes.
//
// Acotado a propósito: 3 intentos y para. Un pedido `inRestaurant` sin fila de asociación (huérfano
// por fallo del backend) NO se puede agrupar desde el front → el retry se detiene (sin polling
// infinito) y queda suelto. Ese límite solo lo cierra fiabilidad de backend.
//
// Se extrae de `useIncomingOrder` para poder testear el agendado/cancelado/cleanup con fake timers
// sin montar todo el hook. El hook crea UNA instancia (vía ref) y le inyecta `forceRefetchOrder` y
// el merge; la instancia mantiene el `Map<id, timers[]>` vivo entre renders.

import { Order, OrderDetail } from '@interfaces/OrderTypes';
import { isEnrichedSelfService } from './selfService';

// Delays ABSOLUTOS (desde el schedule) de cada re-consulta: los 3 intentos disparan a t=2s / 5s / 10s
// (no acumulativos), así que la ventana real de sanado es ~10s con gaps de 2s/3s/5s. Cubre la ventana
// observada de materialización de la asociación de mesa sin machacar el backend.
export const SELF_SERVICE_HEAL_BACKOFF_MS: readonly number[] = [2000, 5000, 10000];

export interface SelfServiceHealer {
  // Agenda el retry para un pedido. Reprograma limpio si ya había intentos en vuelo para ese id.
  schedule: (orderId: string) => void;
  // Cancela los intentos pendientes de un pedido (al enriquecerse o al ignorarlo).
  cancel: (orderId: string) => void;
  // Cancela TODOS los intentos pendientes (cleanup en el unmount del hook).
  cancelAll: () => void;
  // Nº de pedidos con entrada viva en el Map de timers (introspección para tests: verifica que un
  // huérfano no deja la entrada colgada tras el último intento — sin esto solo se veía getTimerCount).
  getPendingCount: () => number;
}

interface HealerDeps {
  // Re-consulta el detalle del pedido (fuente de verdad del `self_service`).
  forceRefetchOrder: (id: string) => Promise<OrderDetail>;
  // Aplica el merge en el/los store(s) cuando el pedido llegó enriquecido.
  onEnriched: (id: string, selfService: NonNullable<Order['self_service']>) => void;
  backoffMs?: readonly number[];
}

export const createSelfServiceHealer = ({
  forceRefetchOrder,
  onEnriched,
  backoffMs = SELF_SERVICE_HEAL_BACKOFF_MS,
}: HealerDeps): SelfServiceHealer => {
  const timers = new Map<string, ReturnType<typeof setTimeout>[]>();

  const cancel = (orderId: string) => {
    const pending = timers.get(orderId);
    if (pending) pending.forEach((t) => clearTimeout(t));
    timers.delete(orderId);
  };

  const cancelAll = () => {
    timers.forEach((pending) => pending.forEach((t) => clearTimeout(t)));
    timers.clear();
  };

  // Un intento de re-consulta: si el read ya trae la asociación, mergea y cancela lo pendiente.
  // Best-effort: nunca rechaza (un intento fallido no rompe la recepción ni los demás intentos).
  // `isLast` marca el último intento del backoff: si al agotarlo el pedido sigue sin enriquecer
  // (huérfano o refetch fallido), borra su entrada del Map para no dejarla colgada de por vida
  // (fuga de memoria lenta en sesiones de tablet POS de larga duración).
  const attempt = async (orderId: string, isLast: boolean) => {
    try {
      const detail = await forceRefetchOrder(orderId);
      if (isEnrichedSelfService(detail.pedido)) {
        onEnriched(orderId, detail.pedido.self_service as NonNullable<Order['self_service']>);
        cancel(orderId); // enriquecido → cancela los intentos restantes y libera la entrada del Map
        return;
      }
    } catch {
      // swallow
    }
    // Último intento sin enriquecer → libera la entrada del Map (los timers ya dispararon; solo
    // queda limpiar el registro para que no crezca sin cota).
    if (isLast) timers.delete(orderId);
  };

  const schedule = (orderId: string) => {
    // Reprograma desde cero si el mismo id ya tenía intentos en vuelo (evita duplicar timers).
    cancel(orderId);
    const lastIndex = backoffMs.length - 1;
    const handles = backoffMs.map((delay, index) =>
      setTimeout(() => {
        attempt(orderId, index === lastIndex).catch(() => {
          /* attempt ya es best-effort; el catch solo evita una promesa flotante */
        });
      }, delay),
    );
    timers.set(orderId, handles);
  };

  const getPendingCount = () => timers.size;

  return { schedule, cancel, cancelAll, getPendingCount };
};
